/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import { defineMessage } from "react-intl";
import type { ScreensConfig } from "tailwindcss/types/config";

import type { Locale } from "./../config/sites";

const NODE_ENV = process.env.NODE_ENV;
export const isProduction = NODE_ENV === "production";

export const DEFAULT_SITE_LOCALE: Locale = {
  id: "default",
  alias: "en-GB",
  preferredCurrency: "GBP",
};

export const DEFAULT_COUNTRY_CODES = { US: "US", GB: "GB" };
export const DEFAULT_CURRENCIES = { USD: "USD", GBP: "GBP" };

export const IMAGES_PREFIX = "https://cdn.media.amplience.net/i/SweatyBetty";
export const ROOT_CATEGORY_ID = "shop";
export const ROOT_CATEGORY_LEVELS = 4;
// import { defineMessage } from "react-intl";
// Constants used in the used for product searching.
export const DEFAULT_SEARCH_PARAMS = {
  limit: 25,
  offset: 0,
  sort: "best-matches",
  refine: [],
};
export const DEFAULT_LIMIT_VALUES = [25, 50, 100]; // Page sizes

// Constants for Search Component
export const RECENT_SEARCH_LIMIT = 5;
export const RECENT_SEARCH_KEY = "recent-search-key";
export const RECENT_SEARCH_MIN_LENGTH = 3;

// Constants for the Homepage's Shop Products section.
export const HOME_SHOP_PRODUCTS_CATEGORY_ID = "newarrivals";
export const HOME_SHOP_PRODUCTS_LIMIT = 10;

export const cssColorGroups = {
  beige: "#d3bca9",
  black: "#000000",
  blue: "#4089c0",
  brown: "#8e6950",
  green: "#88c290",
  grey: "#919191",
  gray: "#919191",
  silver: "#c0c0c0",
  navy: "#000080",
  orange: "#f4995c",
  pink: "#f5a0ca",
  purple: "#9873b9",
  red: "#df5b5b",
  white: "#FFFFFFF",
  yellow: "#fbe85a",
  gold: "#ffd700",
  miscellaneous:
    "linear-gradient(to right, orange , yellow, green, cyan, blue, violet)",
};

// Color to use for the UI surrounding the page in browsers.
export const THEME_COLOR = "#0176D3";

export const FILTER_ACCORDION_SATE = "filters-expanded-index";

// export const API_ERROR_MESSAGE = defineMessage({
//   id: "global.error.something_went_wrong",
//   defaultMessage: "Something went wrong. Try again!",
// });

export const HOME_HREF = "/";

export const MAX_ORDER_QUANTITY = 10;
export const LOW_STOCK_NUM = 3;

export const urlPartPositions = {
  PATH: "path",
  QUERY_PARAM: "query_param",
  NONE: "none",
};

export const DEFAULT_SITE_TITLE = "Retail React App";

export const MAX_CACHE_AGE = 60 * 15; // 15 min

// Toast messages exist outside the scope of the base IntlProvider. This means
// that commonly used components that require localization cannot easily be
// extracted into a hook/helper. However, we can still extract the message
// definitions to a common location (here), so that each message is only defined
// once.

export const TOAST_MESSAGE_ADDED_TO_WISHLIST = defineMessage({
  id: "global.info.added_to_wishlist",
  defaultMessage:
    "{quantity} {quantity, plural, one {item} other {items}} added to wishlist",
});

export const TOAST_ACTION_VIEW_WISHLIST = defineMessage({
  defaultMessage: "View",
  id: "global.link.added_to_wishlist.view_wishlist",
});

export const TOAST_MESSAGE_REMOVED_FROM_WISHLIST = defineMessage({
  id: "global.info.removed_from_wishlist",
  defaultMessage: "Item removed from wishlist",
});

// OCAPI CONFIG
export const OCAPI_HOST = "https://zzra-074.dx.commercecloud.salesforce.com";
export const SITE_ID = "SB";
export const CLIENT_ID = "d741a8f8-1735-41a5-9dfd-e3a50dea5329";
export const CLIENT_SECRET = "London@2025333333";
export const CUSTOMER_LIST_ID = "SB";
export const WISHLIST_LIMIT_REACHED =
  "You have reached the maximum number of products in your Wishlist";
export const ADDED_TO_WISHLIST = "Added to your Wishlist";
export const REMOVED_FROM_WISHLIST = "Removed from your Wishlist";

export const ITEMS_POSITION = {
  left: "items-start",
  right: "items-end",
  center: "items-center",
};

export const JUSTIFY_POSITION = {
  left: "justify-start",
  right: "justify-end",
  center: "justify-center",
};

export const ABSOLUTE_POSITION = {
  left: "inset-y-0 left-0",
  right: "inset-y-0 right-0",
  center: "inset-0",
};

export const ABSOLUTE_POSITION_COMBINED = {
  "left-top": "top-0 left-0",
  "left-bottom": "bottom-0 left-0",
  "right-top": "top-0 right-0",
  "right-bottom": "bottom-0 right-0",
  "center-top": "top-0 left-1/2 -translate-x-1/2",
  "center-bottom": "bottom-0 left-1/2 -translate-x-1/2",
  left: "inset-y-0 left-0",
  right: "inset-y-0 right-0",
  center: "inset-0",
};

export const CAROUSEL_TILE_RIGHT_MARGIN_MOBILE = 12;
export const CAROUSEL_TILE_RIGHT_MARGIN_TABLET = 16;
export const CAROUSEL_TILE_RIGHT_MARGIN_DESKTOP = 20;

export const SCREENS = {
  sm: "360px",
  md: "768px",
  lgAlt: "1024px",
  lg: "1280px",
  xl: "1920px",
} satisfies ScreensConfig;

export const TEXT_ALIGN = {
  left: "text-left",
  right: "text-right",
  center: "text-center",
};
